// Multievento, una única fuente para el precio: el precio unitario, el IVA, el
// descuento y el concepto de cada línea viven en la línea de la cabecera
// (presupuesto.menus_personalizados). La jornada guarda lo propio del día
// (personas, platos, horas, adaptaciones). Mismo criterio que el servidor
// (backend/multievento_utils.py: linea_cabecera_de y linea_jornada_resuelta);
// aquí solo se usa para enseñar los importes, el servidor es quien manda.

export const TEXTO_PRECIO_EN_CABECERA = 'El precio y el IVA se cambian en Líneas y menús';

const CAMPOS_CABECERA = [
  'precio_unitario', 'precio_ud', 'precio_por_persona', 'precio_persona', 'precio_unitario_original',
  'descuento_tipo', 'descuento_valor', 'tipo_iva', 'iva_percent', 'iva', 'concepto_cliente',
  'unidades_cliente_modo', 'unidades_cliente',
];

const texto = (v) => String(v ?? '').trim();

// Precio unitario de venta: precio_unitario > precio_ud > precio_por_persona > precio_persona
export const precioEfectivo = (l) => {
  for (const campo of ['precio_unitario', 'precio_ud', 'precio_por_persona', 'precio_persona']) {
    const v = l?.[campo];
    if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
};

// Línea de venta con sus propias unidades y precio (mismo criterio que el
// servidor: es_linea_venta_aparte): alquiler de material o bebida con importe (D9)
export const esLineaBebida = (l) => !!(l && (l.es_bebida || String(l.menu_id || '').startsWith('bebida:')));
export const esLineaVentaAparte = (l) => !!(l && (l.es_alquiler
  || String(l.menu_id || '').startsWith('alquiler-') || esLineaBebida(l)));

// Clave de una línea: su linea_id (línea propia de la cabecera) o su menú
export const claveLinea = (l) => l?.linea_id || l?.menu_id || l?.nombre_menu || l?.nombre || '';

// Línea de la cabecera de una línea de jornada (null si solo está en jornadas
// o es una línea de alquiler o de bebida con importe, que lleva su propio precio)
export const lineaCabecera = (linea, raiz = []) => {
  if (!linea || linea.es_alquiler || esLineaBebida(linea)) return null;
  const lineas = (raiz || []).filter(Boolean);
  if (linea.linea_id) {
    const exacta = lineas.find(r => r.linea_id === linea.linea_id);
    if (exacta) return exacta;
  }
  let candidatas = linea.menu_id
    ? lineas.filter(r => r.menu_id === linea.menu_id)
    : lineas.filter(r => {
      const n = texto(linea.nombre_menu || linea.nombre);
      return n && texto(r.nombre_menu || r.nombre) === n;
    });
  if (linea.linea_id) candidatas = candidatas.filter(r => !r.linea_id);
  if (candidatas.length === 0) return null;
  if (candidatas.length === 1) return candidatas[0];
  const libres = candidatas.filter(r => !r.linea_id);
  const opciones = libres.length > 0 ? libres : candidatas;
  const precio = precioEfectivo(linea);
  const puntos = (r) => (texto(r.concepto_cliente) === texto(linea.concepto_cliente) ? 2 : 0)
    + (precio !== null && precioEfectivo(r) === precio ? 1 : 0);
  return opciones.reduce((mejor, r) => (puntos(r) > puntos(mejor) ? r : mejor), opciones[0]);
};

// La línea de la jornada con el precio, el IVA, el descuento, el concepto y el
// nombre de su línea de la cabecera
export const lineaResuelta = (linea, raiz = []) => {
  const par = lineaCabecera(linea, raiz);
  if (!par) return linea;
  const res = { ...linea };
  CAMPOS_CABECERA.forEach((campo) => {
    if (Object.prototype.hasOwnProperty.call(par, campo)) res[campo] = par[campo];
    else delete res[campo];
  });
  if (par.nombre_menu) res.nombre_menu = par.nombre_menu;
  if (par.linea_id) res.linea_id = par.linea_id;
  return res;
};

// Líneas de una jornada para los documentos de cocina (minuta, calendario):
// resueltas contra la cabecera y con sus platos o, si no tiene, los de SU línea
// de la cabecera (mismo criterio que el servidor: platos_de_linea_jornada).
// Mismo orden que jornada.menus_personalizados (los ids "menu-<índice>" de la
// minuta se refieren a esta lista).
export const lineasJornadaConPlatos = (jornada, raiz = []) => (
  (Array.isArray(jornada?.menus_personalizados) ? jornada.menus_personalizados : [])
    .filter(l => l && typeof l === 'object')
    .map((linea) => {
      const res = lineaResuelta(linea, raiz);
      if ((linea.platos_ids || []).length > 0) return res;
      const par = lineaCabecera(linea, raiz);
      if (!par || !(par.platos_ids || []).length) return res;
      const propias = linea.unidades_por_plato && Object.keys(linea.unidades_por_plato).length > 0
        ? linea.unidades_por_plato : null;
      return {
        ...res,
        platos_ids: [...par.platos_ids],
        unidades_por_plato: propias || par.unidades_por_plato || {},
        ...(par.es_personalizado && !res.es_personalizado ? { es_personalizado: par.es_personalizado } : {}),
      };
    })
);

// Lo propio del día (o lo que la jornada hereda en vivo de la cabecera si no
// lo tiene: platos y horas): no se copia de la cabecera
const CAMPOS_PROPIOS_DIA = [
  'num_personas', 'uds', 'platos_ids', 'unidades_por_plato', 'platos_cantidades',
  'hora_inicio_servicio', 'hora_fin_servicio', 'hora_servicio_manual', 'requiere_horario', 'adaptaciones',
  'creada_desde_jornadas',
];

// Copia de una línea de la cabecera para una jornada: su precio, IVA,
// concepto, bebida y personal, con las personas de la jornada. Nace con la
// hora de servicio y el tipo de la cabecera si la cabecera los tiene
// (sincronizada, sin hora propia; mismo criterio que el servidor:
// multievento_utils.con_horas_de_cabecera). "Acompaña a" por índice es el de
// la cabecera: no se copia.
export const copiaParaJornada = (lineaCab, numPersonas) => {
  const cab = lineaCab || {};
  const copia = { ...cab };
  CAMPOS_PROPIOS_DIA.forEach((campo) => { delete copia[campo]; });
  delete copia.acompana_a_indice;
  if (cab.hora_inicio_servicio || cab.hora_fin_servicio) {
    if (cab.hora_inicio_servicio) copia.hora_inicio_servicio = cab.hora_inicio_servicio;
    if (cab.hora_fin_servicio) copia.hora_fin_servicio = cab.hora_fin_servicio;
    copia.hora_servicio_manual = false;
  }
  if (typeof cab.requiere_horario === 'boolean') copia.requiere_horario = cab.requiere_horario;
  return { ...copia, num_personas: numPersonas };
};

// Quita la línea `pos` de las líneas de una jornada y cada complemento sigue
// con su servicio: `acompana_a_indice` se corre un puesto si su servicio
// estaba detrás; si se quita su servicio, se quita la elección (vuelve al de
// por defecto) con su `acompana_a_linea_id`. Mismo criterio que
// `remapearAcompana` de la ficha y `horarios_servicios.remapear_acompana`.
export const quitarLineaConAcompana = (lineas, pos) => (Array.isArray(lineas) ? lineas : [])
  .filter((_, i) => i !== pos)
  .map((l) => {
    const idx = l ? l.acompana_a_indice : null;
    if (typeof idx !== 'number' || idx < pos) return l;
    if (idx === pos) {
      const { acompana_a_indice: _indice, acompana_a_linea_id: _lineaId, ...resto } = l;
      return resto;
    }
    return { ...l, acompana_a_indice: idx - 1 };
  });

// ¿Es un multievento con jornadas? (mismo criterio que el servidor: es_multievento)
export const esMultievento = (p) => !!(p?.es_multi_evento && Array.isArray(p?.jornadas) && p.jornadas.length > 0);

const fechaJornada = (j) => String(j?.fecha ?? '').slice(0, 10);

// Jornadas ordenadas por fecha; las que no tienen fecha, al final
export const jornadasOrdenadas = (p) => (Array.isArray(p?.jornadas) ? p.jornadas : [])
  .filter(Boolean)
  .map((j, i) => ({ j, i }))
  .sort((a, b) => {
    const fa = fechaJornada(a.j) || '9999-99-99';
    const fb = fechaJornada(b.j) || '9999-99-99';
    return fa < fb ? -1 : fa > fb ? 1 : a.i - b.i;
  })
  .map(x => x.j);

// Hora de servicio de una línea: [inicio, fin] ('' si no la tiene)
const horasServicio = (l) => [texto(l?.hora_inicio_servicio), texto(l?.hora_fin_servicio)];

// Hora de un servicio en una jornada frente a su línea de la cabecera. Hora
// propia: la jornada tiene hora y es distinta de la de la cabecera (puesta a
// mano o movida al cambiar la hora de inicio de la jornada); esa se respeta
// al cambiar la hora desde el Resumen (mismo criterio que el servidor:
// multievento_lineas_routes.tiene_hora_propia). Sin hora en la jornada NO se
// pinta la de la cabecera como si la tuviera (la orden de cocina nueva y la
// hoja de material leen la de la jornada): `heredada` (no tiene hora) y
// `sinHora` si la cabecera sí la tiene ("Igualar todas" la pone), con la de la
// cabecera en `cabInicio` / `cabFin`.
// { inicio, fin, propia, heredada, sinHora, cabInicio, cabFin }
export const estadoHoraServicioJornada = (lineaJornada, lineaCab) => {
  const [ini, fin] = horasServicio(lineaJornada);
  const [ci, cf] = horasServicio(lineaCab);
  if (!ini && !fin) {
    return { inicio: '', fin: '', propia: false, heredada: true, sinHora: !!(ci || cf), cabInicio: ci, cabFin: cf };
  }
  const propia = ini !== ci || fin !== cf;
  return { inicio: ini, fin, propia, heredada: false, sinHora: false, cabInicio: ci, cabFin: cf };
};

// Horario de un servicio de la cabecera en cada jornada que lo lleva, por
// fecha: [{ jornadaId, numero, fecha, inicio, fin, propia, heredada, sinHora,
// bloqueo, franjas, repetido, refInicio, refFin }]
// `bloqueo`: 'facturada' o 'celebrada' (la hora ya no se cambia ahí) o null.
// `franjas`: el estado de CADA línea del día emparejada con este servicio (F4:
// el mismo servicio dos veces en el día, "09:00 a 10:00 y 17:00 a 18:00");
// inicio, fin y heredada son los de la primera; propia y sinHora, de
// cualquiera. `repetido`: más de una ("Igualar todas" no junta las de hora
// propia de un servicio repetido). `refInicio` / `refFin`: el horario del
// día (el de la jornada o, sin hora, el del evento) para el aviso F6.
// `hoy`: 'AAAA-MM-DD' (hoyMadrid()).
export const horarioServicioPorJornada = (lineaCab, p, hoy = '') => {
  if (!lineaCab) return [];
  const raiz = p?.menus_personalizados || [];
  const res = [];
  jornadasOrdenadas(p).forEach((j, idx) => {
    const lineas = (Array.isArray(j.menus_personalizados) ? j.menus_personalizados : [])
      .filter(l => l && lineaCabecera(l, raiz) === lineaCab);
    if (lineas.length === 0) return;
    const fecha = fechaJornada(j) || null;
    let bloqueo = null;
    if (j.facturada || j.factura_id) bloqueo = 'facturada';
    else if (fecha && hoy && fecha < hoy) bloqueo = 'celebrada';
    const franjas = lineas.map(l => estadoHoraServicioJornada(l, lineaCab));
    res.push({
      jornadaId: j.id, numero: idx + 1, fecha, ...franjas[0],
      propia: franjas.some(x => x.propia), sinHora: franjas.some(x => x.sinHora),
      franjas, repetido: franjas.length > 1,
      refInicio: j.hora_inicio || p?.hora_inicio || '',
      refFin: j.hora_inicio ? (j.hora_fin || '') : (p?.hora_fin || ''),
      bloqueo,
    });
  });
  return res;
};

// Unidades de una línea de la cabecera en cada jornada: la suma de las
// personas de las líneas de esa jornada que se emparejan con ella.
// [{ jornadaId, numero, fecha, uds }] solo con las jornadas donde aparece.
export const unidadesPorJornada = (lineaCab, p) => {
  if (!lineaCab) return [];
  const raiz = p?.menus_personalizados || [];
  const res = [];
  jornadasOrdenadas(p).forEach((j, idx) => {
    const uds = (Array.isArray(j.menus_personalizados) ? j.menus_personalizados : [])
      .filter(l => l && lineaCabecera(l, raiz) === lineaCab)
      .reduce((suma, l) => suma + (Number(l.num_personas) || 0), 0);
    if (uds > 0) res.push({ jornadaId: j.id, numero: idx + 1, fecha: fechaJornada(j) || null, uds });
  });
  return res;
};

// Unidades de una línea de la cabecera en todo el multievento: { total,
// porJornada }. null si no es multievento, si es una línea de alquiler o de
// bebida con importe (lleva sus propias unidades) o si la línea no está en
// ninguna jornada.
export const unidadesMultievento = (lineaCab, p) => {
  if (!esMultievento(p) || !lineaCab || esLineaVentaAparte(lineaCab)) return null;
  const porJornada = unidadesPorJornada(lineaCab, p);
  if (porJornada.length === 0) return null;
  return { total: porJornada.reduce((suma, x) => suma + x.uds, 0), porJornada };
};

// "20 + 35 por jornada" o, con una sola jornada, "35 en la jornada 2"
export const textoUnidadesJornadas = (porJornada) => {
  const lista = Array.isArray(porJornada) ? porJornada : [];
  if (lista.length === 0) return '';
  if (lista.length === 1) return `${lista[0].uds} en la jornada ${lista[0].numero}`;
  return `${lista.map(x => x.uds).join(' + ')} por jornada`;
};

// Rango de fechas para correos y documentos (mismo formato que el servidor,
// multievento_utils.texto_rango_fechas): "17/11/2026" (un día), "17 al
// 18/11/2026" (mismo mes), "30/11 al 01/12/2026" (mismo año) o "30/12/2026 al
// 02/01/2027". '' si ninguna fecha vale.
export const textoRangoFechas = (fechas) => {
  const isos = [...new Set((Array.isArray(fechas) ? fechas : [])
    .map(f => String(f ?? '').slice(0, 10))
    .filter(f => /^\d{4}-\d{2}-\d{2}$/.test(f)))].sort();
  if (isos.length === 0) return '';
  const [a, b] = [isos[0], isos[isos.length - 1]];
  const dmy = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  if (a === b) return dmy(a);
  if (a.slice(0, 7) === b.slice(0, 7)) return `${a.slice(8, 10)} al ${dmy(b)}`;
  if (a.slice(0, 4) === b.slice(0, 4)) return `${a.slice(8, 10)}/${a.slice(5, 7)} al ${dmy(b)}`;
  return `${dmy(a)} al ${dmy(b)}`;
};

// Fecha del evento para correos y documentos: en un multievento, el rango de
// sus jornadas ("17 al 18/11/2026"); en un evento de un día, su fecha_evento
// en dd/mm/aaaa. '' si no hay fecha.
export const textoFechasEvento = (p) => {
  if (esMultievento(p)) {
    const texto = textoRangoFechas(p.jornadas.filter(Boolean).map(fechaJornada));
    if (texto) return texto;
  }
  return textoRangoFechas([p?.fecha_evento]);
};

// Solo el rango de un multievento de varios días ("17 al 18/11/2026"); '' si
// es un evento de un día o todas las jornadas son el mismo día
export const rangoFechasMultievento = (p) => {
  if (!esMultievento(p)) return '';
  const texto = textoFechasEvento(p);
  return texto.includes(' al ') ? texto : '';
};

// Primera jornada por fecha (la que da las horas de la cabecera); null si no
// es multievento
export const primeraJornada = (p) => (esMultievento(p) ? jornadasOrdenadas(p)[0] || null : null);

export const TEXTO_FECHA_EN_JORNADAS = 'Se cambian en las jornadas';

// Alquiler por jornada (D10): opciones del selector del panel de alquiler. La
// primera, "Todo el evento" (alquiler de la cabecera, id null); después una
// por jornada en orden de fecha: "Jornada 1 (17/11)".
export const opcionesAlquilerJornadas = (p) => {
  if (!esMultievento(p)) return [];
  return [
    { id: null, etiqueta: 'Todo el evento' },
    ...jornadasOrdenadas(p).map((j, idx) => {
      const f = fechaJornada(j);
      return { id: j.id, etiqueta: `Jornada ${idx + 1}${f ? ` (${f.slice(8, 10)}/${f.slice(5, 7)})` : ''}` };
    }),
  ];
};

// Resumen del alquiler propio de cada jornada con lo que ya trae el
// presupuesto (sin más peticiones): [{ jornadaId, etiqueta, lineas, validado,
// texto }] solo de las jornadas con líneas de alquiler. Texto: "Jornada 2: 3
// líneas, sin validar".
export const resumenAlquilerJornadas = (p) => {
  if (!esMultievento(p)) return [];
  const res = [];
  jornadasOrdenadas(p).forEach((j, idx) => {
    const lineas = (Array.isArray(j.materiales_alquiler) ? j.materiales_alquiler : []).filter(Boolean).length;
    if (lineas === 0) return;
    const validado = !!(j.alquiler_validado && typeof j.alquiler_validado === 'object');
    const etiqueta = `Jornada ${idx + 1}`;
    res.push({
      jornadaId: j.id,
      etiqueta,
      lineas,
      validado,
      texto: `${etiqueta}: ${lineas} ${lineas === 1 ? 'línea' : 'líneas'}, ${validado ? 'validado' : 'sin validar'}`,
    });
  });
  return res;
};

// Fecha local de hoy en 'YYYY-MM-DD' (sin pasar por UTC)
const hoyIso = (hoy = new Date()) => {
  const d = hoy instanceof Date ? hoy : new Date(hoy);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Última jornada por fecha (la que cierra el evento); null si no es
// multievento o ninguna jornada tiene fecha
export const ultimaJornada = (p) => {
  if (!esMultievento(p)) return null;
  const conFecha = jornadasOrdenadas(p).filter(j => /^\d{4}-\d{2}-\d{2}$/.test(fechaJornada(j)));
  return conFecha.length > 0 ? conFecha[conFecha.length - 1] : null;
};

// Último día del evento 'YYYY-MM-DD': en un multievento, el de su última
// jornada; en un evento de un día, su fecha_evento. '' si no hay fecha.
export const fechaFinEvento = (p) => {
  const ultima = ultimaJornada(p);
  if (ultima) return fechaJornada(ultima);
  const f = String(p?.fecha_evento ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(f) ? f : '';
};

// ¿El evento ya se celebró? (M-101, mismo criterio que el servidor:
// multievento_utils.es_presupuesto_pasado) La última fecha del evento (la
// última jornada en un multievento, fecha_evento en uno de un día) es
// anterior a hoy. Sin ninguna fecha, no es pasado. Un evento pasado es de
// solo lectura: la ficha usa sus datos guardados y no recalcula nada.
export const eventoPasado = (p, hoy = new Date()) => {
  const fechas = [String(p?.fecha_evento ?? '').slice(0, 10)];
  if (esMultievento(p)) p.jornadas.filter(Boolean).forEach(j => fechas.push(fechaJornada(j)));
  const validas = fechas.filter(f => /^\d{4}-\d{2}-\d{2}$/.test(f)).sort();
  if (validas.length === 0) return false;
  return validas[validas.length - 1] < hoyIso(hoy);
};

// Horas con las que termina el evento (M-99): en un multievento, las de su
// última jornada (o, si no las tiene, las de la cabecera); en un evento de
// un día, las suyas. { horaInicio, horaFin }
export const horasFinEvento = (p) => {
  const ultima = ultimaJornada(p);
  return {
    horaInicio: (ultima && ultima.hora_inicio) || p?.hora_inicio || '',
    horaFin: (ultima && ultima.hora_fin) || p?.hora_fin || '',
  };
};

// Personas por defecto de una jornada nueva o de una línea nueva (M-98): en
// un multievento, las de la jornada indicada o, si no, las de la última
// jornada con personas (la cabecera es la suma de las jornadas, D1); sin
// jornadas o en un evento de un día, las del presupuesto. `defecto` si no
// hay ninguna.
export const personasPorDefectoLinea = (p, jornada = null, defecto = 0) => {
  const propias = Number(jornada?.num_personas) || 0;
  if (propias > 0) return propias;
  const jornadas = Array.isArray(p?.jornadas) ? p.jornadas.filter(Boolean) : [];
  if (p?.es_multi_evento && jornadas.length > 0) {
    const conPersonas = jornadasOrdenadas(p).filter(j => (Number(j?.num_personas) || 0) > 0);
    if (conPersonas.length > 0) return Number(conPersonas[conPersonas.length - 1].num_personas);
    return defecto;
  }
  return Number(p?.num_personas) || defecto;
};
